// src/shared/composables/usePageTitle.ts

import { globalComposer } from '@/i18n';
import { resolveProductName } from '@/shared/constants/brand';
import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';

/**
 * Composable for managing page titles and meta tags dynamically.
 *
 * Features:
 * - Automatic title updates with app name suffix
 * - i18n support for translated titles
 * - Meta tag updates (og:title, twitter:title)
 * - TypeScript support for route meta
 * - Can be used outside of component setup (e.g., in router guards)
 *
 * @example
 * // In a component
 * const { setTitle } = usePageTitle();
 * setTitle('Custom Page');
 *
 * @example
 * // With i18n key
 * const { setTitle } = usePageTitle();
 * setTitle('DASHBOARD.TITLE'); // Will translate automatically
 *
 * @example
 * // In router guards
 * const { setTitle } = usePageTitle();
 * router.afterEach((to) => {
 *   setTitle(to.meta.title);
 * });
 */

const TITLE_SEPARATOR = ' | ';

export function usePageTitle() {
  // Use the global i18n composer instance directly to avoid requiring
  // Vue's injection context. This allows usePageTitle to be called
  // outside of component setup (e.g., in router guards).
  const { t, te } = globalComposer;

  const bootstrapStore = useBootstrapStore();
  const { display_domain, brand_product_name, domain_strategy } = storeToRefs(bootstrapStore);

  // Cache DOM elements to avoid repeated queries
  let ogTitleMeta: HTMLMetaElement | null | undefined;
  let twitterTitleMeta: HTMLMetaElement | null | undefined;

  /**
   * Resolves the app name used in the document title, og:title and
   * twitter:title.
   *
   * PLATFORM (canonical deploy, domain_strategy !== 'custom'): the configured
   * brand product name ('SeuID') wins over the bare canonical domain, so tab
   * titles read as the product, not the hostname. This mirrors the server's
   * page_title precedence (brand identity over display_domain on the platform).
   *
   * TENANT custom domains (domain_strategy === 'custom'): the tenant's
   * display_domain wins. brand_product_name in bootstrap is the INSTALL-wide
   * platform brand, not the tenant's, so it must never override a tenant's own
   * domain in its browser tabs.
   *
   * Fallback chain (when the preferred value is unset):
   *   display_domain -> resolveProductName(brand_product_name) -> neutral default
   *
   * This must NEVER emit OTS branding. When nothing is configured the title
   * degrades to the neutral default ('Secure Links' via resolveProductName)
   * rather than leaking "Onetime Secret" (the A4 leak). The product-name
   * fallback goes through the shared `resolveProductName` helper — the same one
   * `identityStore.productName` uses — rather than the store itself, because
   * usePageTitle runs in router guards (see setupRouterGuards), outside the
   * component/i18n context that identityStore initializes with.
   *
   * Reads `.value` on each call so the resolved name stays reactive to store
   * changes (do not hoist the raw values out of the refs).
   */
  const getAppName = (): string => {
    const productName = brand_product_name?.value;
    const isCustomDomain = domain_strategy?.value === 'custom';

    // On the platform, a configured product name outranks the bare domain.
    if (!isCustomDomain && productName) {
      return productName;
    }

    return display_domain.value || resolveProductName(productName);
  };

  /**
   * Translates a title if it's an i18n key, otherwise returns the raw string
   * @param title - Either a plain string or an i18n key
   * @returns Translated or original title
   */
  const translateTitle = (title: string): string => {
    // Check if it's an i18n key (e.g., 'DASHBOARD.TITLE')
    if (te(title)) {
      try {
        return t(title);
      } catch (error) {
        console.error('Failed to translate title:', title, error);
        return title;
      }
    }
    return title;
  };

  /**
   * Formats the complete page title with app name suffix
   * @param pageTitle - The page-specific title
   * @returns Formatted title string
   */
  const formatTitle = (pageTitle: string): string => {
    const appName = getAppName();
    const translatedTitle = translateTitle(pageTitle);

    if (!translatedTitle || translatedTitle === appName) {
      return appName;
    }

    // The brand already leads the title (the Stripe-style homepage
    // "SeuID — Compartilhe…"): don't append the app name again, which would
    // read "SeuID — … | SeuID".
    if (translatedTitle.startsWith(appName)) {
      return translatedTitle;
    }

    return `${translatedTitle}${TITLE_SEPARATOR}${appName}`;
  };

  /**
   * Homepage document title. Like Stripe ("Stripe | Financial Infrastructure
   * for the Internet"), the home leads with the brand + value proposition, not
   * "Início | SeuID". On the PLATFORM this is `${appName} — ${tagline}`,
   * matching the server-rendered BRAND_PAGE_TITLE so crawlers and the tab agree.
   * On a TENANT custom domain it returns null, so the caller falls back to the
   * normal per-route title and the SeuID tagline never leaks onto a tenant.
   */
  const platformHomeTitle = (): string | null => {
    const isPlatform = domain_strategy?.value !== 'custom' && !!brand_product_name?.value;
    if (!isPlatform) return null;
    const appName = getAppName();
    if (!te('web.COMMON.brand_tagline')) return appName;
    return `${appName} — ${t('web.COMMON.brand_tagline')}`;
  };

  /**
   * Updates the document title and related meta tags
   * @param title - The new title (can be plain string or i18n key)
   */
  const setTitle = (title: string | null | undefined) => {
    try {
      const finalTitle = title ? formatTitle(title) : getAppName();

      // Update document title
      document.title = finalTitle;

      // Cache and update Open Graph meta tag (query once on first access)
      if (ogTitleMeta === undefined) {
        ogTitleMeta = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
      }
      if (ogTitleMeta) {
        ogTitleMeta.setAttribute('content', finalTitle);
      }

      // Cache and update Twitter meta tag (query once on first access)
      if (twitterTitleMeta === undefined) {
        twitterTitleMeta = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]');
      }
      if (twitterTitleMeta) {
        twitterTitleMeta.setAttribute('content', finalTitle);
      }
    } catch (error) {
      console.error('Failed to update page title:', error);
    }
  };

  /**
   * Creates a computed title that automatically updates when dependencies change
   * Useful for reactive titles based on component state
   *
   * @param titleGetter - Function that returns the current title
   * @returns Computed title value
   *
   * @example
   * const secretId = ref('abc123');
   * const title = useComputedTitle(() => `Secret ${secretId.value}`);
   * watch(title, (newTitle) => setTitle(newTitle));
   */
  const useComputedTitle = (titleGetter: () => string) => {
    const title = computed(titleGetter);

    watch(title, (newTitle) => {
      setTitle(newTitle);
    }, { immediate: true });

    return title;
  };

  return {
    setTitle,
    useComputedTitle,
    formatTitle,
    platformHomeTitle,
  };
}
